import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Link, useLocation } from "react-router-dom";

const Header = ({ data }) => {
  const location = useLocation();

  const [isFixed, setIsFixed] = useState(false);
  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || "light",
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const hoverTimeoutRef = useRef(null);

  const nestedMenuData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const menu = [];
    const map = {};

    data.forEach((item) => {
      map[item.ID] = { ...item, children: [] };
    });

    data.forEach((item) => {
      if (item.menu_item_parent === "0") {
        menu.push(map[item.ID]);
      } else {
        if (map[item.menu_item_parent]) {
          map[item.menu_item_parent].children.push(map[item.ID]);
        }
      }
    });
    return menu;
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".sky47_dropdown")) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsFixed(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.classList.remove("light_theme", "dark_theme");
    document.body.classList.add(
      theme === "dark" ? "dark_theme" : "light_theme",
    );
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdownId(null);
    document.body.classList.remove("head_menu_open_body");
  }, [location.pathname]);

  const handleThemeChange = useCallback((newTheme) => {
    setTheme(newTheme);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => {
      const newState = !prev;
      if (newState) {
        window.scrollTo(0, 0);
        document.body.classList.add("head_menu_open_body");
      } else {
        document.body.classList.remove("head_menu_open_body");
      }
      return newState;
    });
  }, []);

  const closeMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
    document.body.classList.remove("head_menu_open_body");
  }, []);

  return (
    <header className={`custom_header ${isFixed ? "fixed_header" : ""}`}>
      <div className="header_content">
        <div className="nav_logo">
          <Link to="/" aria-label="Go to homepage">
            <img
              src="/assets/img/sky-47.webp"
              alt="Sky47 Logo"
              width={108}
              height={60}
            />
          </Link>
        </div>

        <div className="right_header_content">
          <nav
            className={`header_nav ${isMobileMenuOpen ? "open_mob_menu" : ""}`}
          >
            <div className="custom_menu_toggle" onClick={toggleMobileMenu}>
              ☰
            </div>
            <div className="custom_menu_navbar_icon" onClick={toggleMobileMenu}>
              ×
            </div>

            <ul>
              {nestedMenuData?.map((item) => {
                // Check if Parent is active or if any child is currently active
                const isExactParentActive = location.pathname === item.url;
                const isAnyChildActive = item.children?.some(child => location.pathname === child.url);
                const isParentActive = isExactParentActive || isAnyChildActive;

                const isLast = item.url === "/contact";
                const hasChildren = item.children && item.children.length > 0;
                const isDropdownOpen = openDropdownId === item.ID;

                return (
                  <li
                    key={item.ID}
                    className={`${isParentActive ? "active" : ""} ${isLast ? "Contact_mob_btn" : ""} ${hasChildren ? "sky47_dropdown" : ""}`}
                    onMouseEnter={() => {
                      if (hasChildren && window.innerWidth > 991) {
                        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                        setOpenDropdownId(item.ID);
                      }
                    }}
                    onMouseLeave={() => {
                      if (hasChildren && window.innerWidth > 991) {
                        hoverTimeoutRef.current = setTimeout(() => {
                          setOpenDropdownId(null);
                        }, 250);
                      }
                    }}
                  >
                    <Link
                      to={item.url}
                      className={`${hasChildren ? "sky47_dropdown_toggle" : ""} ${isParentActive ? "active_link" : ""}`}
                      onClick={() => closeMenu()}
                    >
                      {item.title}
                      {hasChildren && (
                        <span
                          style={{ fontSize: "12px", marginLeft: "6px", cursor: "pointer" }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenDropdownId(isDropdownOpen ? null : item.ID);
                          }}
                        >
                          ▼
                        </span>
                      )}
                    </Link>

                    {hasChildren && (
                      <div className={`sky47_dropdown_container ${isDropdownOpen ? "sky47_show" : ""}`}>
                        {/* Invisible bridge to keep hover alive */}
                        <div style={{ position: "absolute", top: "-20px", left: 0, right: 0, height: "20px" }}></div>

                        {item.children.map((child) => {
                          const isChildActive = location.pathname === child.url;
                          return (
                            <Link
                              key={child.ID}
                              className={`sky47_dropdown_item ${isChildActive ? "active_child" : ""}`}
                              to={child.url}
                              onClick={() => {
                                closeMenu();
                                setOpenDropdownId(null);
                              }}
                            >
                              {child.title}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="reserved_main_mob">
              <a href="/">
                <img src="assets/img/sky-47.webp" alt="Sky47" width={120} height={40} />
              </a>
              <p>Sky47 Copyright Reserved</p>
            </div>
          </nav>

          {/* ... Baqi theme changer aur button logic same rahegi ... */}
          <div className="contact_cta_main">
            <div className="theme_changer">
              <a
                href="#"
                className={`dark_btn ${theme === "dark" ? "active" : ""}`}
                onClick={(e) => { e.preventDefault(); handleThemeChange("dark"); }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none"><path d="M18.4857 11.3081C18.3919 11.214 18.2742 11.1473 18.1453 11.1151C18.0164 11.0828 17.8811 11.0863 17.754 11.1251C16.3588 11.5469 14.8753 11.5823 13.4615 11.2275C12.0478 10.8726 10.7568 10.1409 9.7261 9.11022C8.69543 8.07954 7.96369 6.78855 7.60885 5.3748C7.25402 3.96106 7.28938 2.47753 7.71118 1.0823C7.75033 0.955169 7.75408 0.819768 7.72202 0.690663C7.68997 0.561557 7.62333 0.443632 7.52927 0.349569C7.4352 0.255507 7.31728 0.188865 7.18817 0.156813C7.05907 0.12476 6.92367 0.128509 6.79653 0.167655C4.86757 0.758559 3.1741 1.9428 1.95713 3.55185C0.892858 4.96485 0.243679 6.64663 0.0825273 8.40824C-0.0786242 10.1699 0.254636 11.9415 1.04487 13.5242C1.8351 15.1068 3.051 16.4377 4.55595 17.3675C6.0609 18.2972 7.79529 18.7888 9.56426 18.7871C11.628 18.7935 13.6369 18.1226 15.2826 16.8774C16.8917 15.6604 18.0759 13.9669 18.6668 12.038C18.7055 11.9113 18.7091 11.7765 18.6772 11.648C18.6453 11.5194 18.5791 11.4019 18.4857 11.3081ZM14.4027 15.7084C12.8529 16.8758 10.9336 17.444 8.99804 17.3084C7.06252 17.1729 5.24109 16.3428 3.86906 14.9709C2.49702 13.5989 1.66674 11.7776 1.53106 9.84206C1.39538 7.90655 1.96342 5.98716 3.13062 4.43723C3.89107 3.433 4.87419 2.61896 6.00262 2.05915C5.93834 2.51028 5.90594 2.96537 5.90567 3.42106C5.90833 5.94307 6.91137 8.36102 8.69471 10.1444C10.478 11.9277 12.896 12.9307 15.418 12.9334C15.8746 12.9333 16.3306 12.9009 16.7827 12.8364C16.2223 13.9651 15.4076 14.9482 14.4027 15.7084Z" fill="white" /></svg>
              </a>
              <a
                href="#"
                className={`light_btn ${theme === "light" ? "active" : ""}`}
                onClick={(e) => { e.preventDefault(); handleThemeChange("light"); }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="23" viewBox="0 0 24 23" fill="none"><path d="M10.9683 3.23879V0.970464C10.9683 0.769932 11.048 0.577613 11.1898 0.435815C11.3316 0.294017 11.5239 0.214355 11.7244 0.214355C11.925 0.214355 12.1173 0.294017 12.2591 0.435815C12.4009 0.577613 12.4806 0.769932 12.4806 0.970464V3.23879C12.4806 3.43932 12.4009 3.63164 12.2591 3.77344C12.1173 3.91524 11.925 3.9949 11.7244 3.9949C11.5239 3.9949 11.3316 3.91524 11.1898 3.77344C11.048 3.63164 10.9683 3.43932 10.9683 3.23879ZM17.7733 11.556C17.7733 12.7523 17.4186 13.9218 16.7539 14.9166C16.0892 15.9113 15.1445 16.6866 14.0393 17.1444C12.934 17.6022 11.7177 17.722 10.5444 17.4886C9.371 17.2552 8.2932 16.6791 7.44725 15.8332C6.6013 14.9872 6.0252 13.9094 5.7918 12.7361C5.55841 11.5627 5.67819 10.3465 6.13602 9.24119C6.59384 8.1359 7.36914 7.1912 8.36387 6.52654C9.35861 5.86188 10.5281 5.50712 11.7244 5.50712C13.3282 5.50887 14.8657 6.14672 15.9997 7.28073C17.1337 8.41473 17.7716 9.95227 17.7733 11.556ZM16.2611 11.556C16.2611 10.6587 15.995 9.78161 15.4965 9.03556C14.998 8.28951 14.2895 7.70804 13.4605 7.36467C12.6316 7.0213 11.7194 6.93146 10.8394 7.10651C9.95937 7.28156 9.15101 7.71363 8.51655 8.34809C7.88209 8.98255 7.45001 9.79091 7.27496 10.6709C7.09992 11.551 7.18976 12.4631 7.53313 13.2921C7.87649 14.1211 8.45797 14.8296 9.20402 15.3281C9.95007 15.8266 10.8272 16.0926 11.7244 16.0926C12.9273 16.0914 14.0804 15.613 14.931 14.7625C15.7815 13.912 16.2599 12.7588 16.2611 11.556ZM5.14063 6.04207C5.28251 6.18394 5.47493 6.26365 5.67558 6.26365C5.87622 6.26365 6.06865 6.18394 6.21052 6.04207C6.3524 5.90019 6.4321 5.70776 6.4321 5.50712C6.4321 5.30647 6.3524 5.11405 6.21052 4.97217L4.6983 3.45995C4.55643 3.31808 4.364 3.23837 4.16336 3.23837C3.96271 3.23837 3.77029 3.31808 3.62841 3.45995C3.48653 3.60183 3.40683 3.79426 3.40683 3.9949C3.40683 4.19554 3.48653 4.38797 3.62841 4.52985L5.14063 6.04207ZM5.14063 17.0699L3.62841 18.5821C3.48653 18.724 3.40683 18.9164 3.40683 19.1171C3.40683 19.3177 3.48653 19.5102 3.62841 19.652C3.77029 19.7939 3.96271 19.8736 4.16336 19.8736C4.364 19.8736 4.55643 19.7939 4.6983 19.652L6.21052 18.1398C6.28077 18.0696 6.3365 17.9862 6.37452 17.8944C6.41254 17.8026 6.4321 17.7042 6.4321 17.6049C6.4321 17.5055 6.41254 17.4071 6.37452 17.3154C6.3365 17.2236 6.28077 17.1402 6.21052 17.0699C6.14027 16.9997 6.05687 16.9439 5.96509 16.9059C5.8733 16.8679 5.77492 16.8483 5.67558 16.8483C5.57623 16.8483 5.47785 16.8679 5.38606 16.9059C5.29428 16.9439 5.21088 16.9997 5.14063 17.0699ZM17.7733 6.26323C17.8726 6.26331 17.971 6.24381 18.0628 6.20587C18.1546 6.16792 18.238 6.11226 18.3083 6.04207L19.8205 4.52985C19.9624 4.38797 20.0421 4.19554 20.0421 3.9949C20.0421 3.79426 19.9624 3.60183 19.8205 3.45995C19.6786 3.31808 19.4862 3.23837 19.2855 3.23837C19.0849 3.23837 18.8925 3.31808 18.7506 3.45995L17.2384 4.97217C17.1325 5.07792 17.0604 5.2127 17.0312 5.35945C17.002 5.50619 17.0169 5.65832 17.0742 5.79655C17.1315 5.93478 17.2285 6.05292 17.3529 6.13599C17.4774 6.21906 17.6237 6.26335 17.7733 6.26323ZM18.3083 17.0699C18.1664 16.928 17.974 16.8483 17.7733 16.8483C17.5727 16.8483 17.3802 16.928 17.2384 17.0699C17.0965 17.2118 17.0168 17.4042 17.0168 17.6049C17.0168 17.8055 17.0965 17.9979 17.2384 18.1398L18.7506 19.652C18.8208 19.7223 18.9042 19.778 18.996 19.816C19.0878 19.854 19.1862 19.8736 19.2855 19.8736C19.3849 19.8736 19.4833 19.854 19.575 19.816C19.6668 19.778 19.7502 19.7223 19.8205 19.652C19.8907 19.5818 19.9465 19.4984 19.9845 19.4066C20.0225 19.3148 20.0421 19.2164 20.0421 19.1171C20.0421 19.0177 20.0225 18.9194 19.9845 18.8276C19.9465 18.7358 19.8907 18.6524 19.8205 18.5821L18.3083 17.0699ZM4.16336 11.556C4.16336 11.3555 4.0837 11.1631 3.9419 11.0213C3.8001 10.8795 3.60778 10.7999 3.40725 10.7999H1.13892C0.938389 10.7999 0.74607 10.8795 0.604272 11.0213C0.462474 11.1631 0.382813 11.3555 0.382812 11.556C0.382813 11.7565 0.462474 11.9488 0.604272 12.0906C0.74607 12.2324 0.938389 12.3121 1.13892 12.3121H3.40725C3.60778 12.3121 3.8001 12.2324 3.9419 12.0906C4.0837 11.9488 4.16336 11.7565 4.16336 11.556ZM11.7244 19.1171C11.5239 19.1171 11.3316 19.1967 11.1898 19.3385C11.048 19.4803 10.9683 19.6727 10.9683 19.8732V22.1415C10.9683 22.342 11.048 22.5344 11.1898 22.6762C11.3316 22.818 11.5239 22.8976 11.7244 22.8976C11.925 22.8976 12.1173 22.818 12.2591 22.6762C12.4009 22.5344 12.4806 22.342 12.4806 22.1415V19.8732C12.4806 19.6727 12.4009 19.4803 12.2591 19.3385C12.1173 19.1967 11.925 19.1171 11.7244 19.1171ZM22.31 10.7999H20.0416C19.8411 10.7999 19.6488 10.8795 19.507 11.0213C19.3652 11.1631 19.2855 11.3555 19.2855 11.556C19.2855 11.7565 19.3652 11.9488 19.507 12.0906C19.6488 12.2324 19.8411 12.3121 20.0416 12.3121H22.31C22.5105 12.3121 22.7028 12.2324 22.8446 12.0906C22.9864 11.9488 23.0661 11.7565 23.0661 11.556C23.0661 11.3555 22.9864 11.1631 22.8446 11.0213C22.7028 10.8795 22.5105 10.7999 22.31 10.7999Z" fill="white" /></svg>
              </a>
            </div>
            <Link className="white_btn" to="/contact">Contact Us</Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;